import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Mail, Globe, Lock, Database, Cookie, Eye, FileText, Megaphone } from 'lucide-react';
import Footer from '@/components/Footer';

export default function Privacy() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-grow max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/" className="text-blue-600 hover:text-blue-700 mb-4 inline-block">
            ← {t('common.back', 'Zurück')}
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Datenschutzerklärung</h1>
          <p className="text-gray-600">Angaben gemäß Datenschutz-Grundverordnung (DSGVO)</p>
          <p className="text-sm text-gray-500 mt-2">
            <strong>Stand:</strong> Juni 2026
          </p>
        </div>

        <div className="space-y-6">
          {/* 1. Verantwortlicher */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                1. Verantwortlicher
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="font-semibold text-gray-900">ivoliq GmbH</p>
              <p className="text-gray-700">vertreten durch den Geschäftsführer Wilhelm Wagner</p>
              <p className="text-gray-700">Raffelspergergasse 11/2/2</p>
              <p className="text-gray-700">1190 Wien, Österreich</p>
              <p className="text-gray-700">FN 680579 b · Handelsgericht Wien</p>
              <p className="text-gray-700 mt-3">
                <strong>E-Mail:</strong>{' '}
                <a href="mailto:datenschutz@ivoliq.io" className="text-blue-600 hover:underline" data-testid="privacy-contact-email">
                  datenschutz@ivoliq.io
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Website:</strong>{' '}
                <a href="https://www.ivoliq.io" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                  www.ivoliq.io
                </a>
              </p>
              <p className="text-gray-700">
                <strong>UID-Nummer:</strong> <span className="italic text-gray-500">in Beantragung (wird nach Zuteilung nachgetragen)</span>
              </p>
            </CardContent>
          </Card>

          {/* 2. Allgemeines */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                2. Allgemeines zur Datenverarbeitung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Umfang der Verarbeitung</h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Wir verarbeiten personenbezogene Daten unserer Nutzer nur, soweit dies zur Bereitstellung einer funktionsfähigen Website und unserer Inhalte und Leistungen erforderlich ist.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlagen</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</li>
                  <li>Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</li>
                  <li>Art. 6 Abs. 1 lit. f DSGVO (Berechtigtes Interesse)</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* 3. Website und Logfiles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-blue-600" />
                3. Bereitstellung der Website
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Erhobene Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>IP-Adresse des Nutzers</li>
                  <li>Datum und Uhrzeit des Zugriffs</li>
                  <li>Browsertyp und -version</li>
                  <li>Betriebssystem</li>
                  <li>Referrer URL</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Gewährleistung der Systemsicherheit und eines reibungslosen Verbindungsaufbaus</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. f DSGVO (Berechtigtes Interesse)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">7 Tage, dann automatische Löschung</p>
              </div>
            </CardContent>
          </Card>

          {/* 4. Registrierung */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                4. Registrierung und Nutzerkonto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>E-Mail-Adresse (Pflicht)</li>
                  <li>Name (optional)</li>
                  <li>Passwort (verschlüsselt)</li>
                  <li>{t('privacy.taxResidency', 'Land der Steueransässigkeit')}</li>
                  <li>Registrierungszeitpunkt</li>
                  <li>Plan/Abonnement</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">{t('privacy.dataUsageAccount', 'Kontoverwaltung und Bereitstellung vertraglicher Leistungen (Steuerberechnungen, Projektverwaltung, Zeiterfassung)')}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">Bis zur Löschung des Nutzerkontos durch den Nutzer</p>
              </div>
            </CardContent>
          </Card>

          {/* 5. Cookies */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="h-5 w-5 text-blue-600" />
                5. Cookies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">5.1 Notwendige Cookies (ohne Einwilligung)</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Authentifizierungs-Token:</strong> Zur Speicherung deiner Login-Session
                </p>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
                </p>
                <p className="text-gray-700 text-sm">
                  <strong>Speicherdauer:</strong> 7 Tage oder bis zum Logout
                </p>
              </div>
              <div className="pt-4 border-t">
                <h4 className="font-semibold text-gray-900 mb-3">5.2 Analytics Cookies (mit Einwilligung)</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-gray-700 text-sm mb-1">
                      <strong>Google Analytics 4:</strong> Zur Analyse des Nutzerverhaltens, Seitenaufrufe, Verweildauer
                    </p>
                    <p className="text-gray-700 text-sm mb-1">
                      <strong>Cookies:</strong> _ga, _ga_*, _gid
                    </p>
                    <p className="text-gray-700 text-sm">
                      <strong>Speicherdauer:</strong> Bis zu 2 Jahre
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-700 text-sm mb-1">
                      <strong>Microsoft Clarity:</strong> Zur Aufzeichnung von Nutzerverhalten (Heatmaps, Session Recordings)
                    </p>
                    <p className="text-gray-700 text-sm mb-1">
                      <strong>Cookies:</strong> _clck, _clsk, CLID, ANONCHK, MR, MUID, SM
                    </p>
                    <p className="text-gray-700 text-sm">
                      <strong>Speicherdauer:</strong> Bis zu 1 Jahr
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-700 text-sm mb-1">
                      <strong>ivoliq-Nutzungsstatistik (First-Party):</strong> Anonyme Seitenaufrufe und pseudonyme Produktnutzung auf eigenen Servern — ohne Cookies und ohne Speicherung auf deinem Gerät
                    </p>
                    <p className="text-gray-700 text-sm mb-1">
                      <strong>Cookies:</strong> Keine
                    </p>
                    <p className="text-gray-700 text-sm">
                      <strong>Speicherdauer:</strong> Rohdaten max. 12 Monate, danach nur aggregierte Statistik
                    </p>
                  </div>
                  <p className="text-gray-700 text-sm mt-3">
                    <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)
                  </p>
                </div>
              </div>
              <div className="pt-4 border-t">
                <h4 className="font-semibold text-gray-900 mb-3">5.3 Marketing Cookies (mit Einwilligung)</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Google Ads Conversion-Tracking:</strong> Zur Messung der Wirksamkeit unserer Werbekampagnen
                </p>
                <div className="text-sm text-gray-700 space-y-2 mb-3">
                  <div>
                    <strong>Cookies:</strong> _gcl_au, _gac_GB-* (Google Ads Cookies)
                  </div>
                  <div>
                    <strong>Speicherdauer:</strong> 90 Tage
                  </div>
                  <div>
                    <strong>Anbieter:</strong> Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland
                  </div>
                  <div>
                    <strong>Zweck:</strong> Conversion-Messung, Werbe-Erfolg tracken
                  </div>
                  <div>
                    <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)
                  </div>
                </div>
                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-sm text-orange-800 mb-2">
                    <strong>⚠️ Hinweis zur Datenübermittlung in die USA:</strong>
                  </p>
                  <p className="text-sm text-orange-800">
                    Google LLC (Muttergesellschaft von Google Ireland) verarbeitet Daten in den USA. Google ist nach dem EU-US Data Privacy Framework zertifiziert.
                  </p>
                </div>
                <p className="text-sm text-gray-700 mt-3">
                  Weitere Informationen: <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Datenschutzerklärung</a>
                </p>
              </div>
              <div className="pt-4 border-t">
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Hinweis:</strong> Du kannst deine Einwilligung jederzeit widerrufen über unsere Cookie-Einstellungen oder durch Löschen der Cookies in deinen Browser-Einstellungen.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 6. Analytics */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-600" />
                6. Web- und Produkt-Analyse (First-Party, ivoliq)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">Eigene Systeme von ivoliq (First-Party) — es werden keine Analyse-Daten an Drittanbieter übertragen. Die Verarbeitung erfolgt auf unseren Servern in der EU.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>Anonyme Seitenaufrufe: aufgerufene Seite, Referrer, Kampagnenparameter, Gerätetyp — ohne IP-Speicherung, ohne Cookies, ohne Kennungen auf deinem Gerät</li>
                  <li>Bei eingeloggten Nutzern: pseudonyme Nutzungsereignisse (z. B. „Beleg erfasst“, „Rechnung erstellt“) mit interner Nutzer-ID — keine E-Mail-Adresse, keine Inhalte deiner Belege oder Rechnungen</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Verbesserung der Anwendung: Verständnis, welche Funktionen genutzt werden, und Optimierung von Bedienbarkeit und Angebot.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">
                  Anonyme Seitenstatistik: keine Verarbeitung personenbezogener Daten. Pseudonyme Produktnutzung eingeloggter Konten:
                  Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Produktverbesserung). Es erfolgt kein Zugriff auf Informationen
                  in deinem Endgerät iSd § 25 TDDDG / § 165 Abs 3 TKG — daher keine Einwilligungspflicht.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Widerspruch (Opt-out)</h4>
                <p className="text-gray-700 text-sm">
                  Du kannst der pseudonymen Produkt-Nutzungsanalyse jederzeit widersprechen — formlos an{' '}
                  <a href="mailto:privacy@ivoliq.io" className="text-blue-600 hover:underline">privacy@ivoliq.io</a>.
                  Dein Konto wird dann von der Auswertung ausgenommen.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 7. Microsoft Clarity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-600" />
                7. Microsoft Clarity (Heatmaps & Session Recordings)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Beschreibung</h4>
                <p className="text-gray-700 text-sm">
                  Wir nutzen Microsoft Clarity, um zu verstehen, wie du mit unserer Website interagierst. Clarity zeichnet das Nutzerverhalten mittels Heatmaps und Session Recordings auf.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">
                  Microsoft Corporation, One Microsoft Way, Redmond, WA 98052-6399, USA<br />
                  EU-Repräsentant: Microsoft Ireland Operations Limited, One Microsoft Place, South County Business Park, Leopardstown, Dublin 18, D18 P521, Irland
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>IP-Adresse (anonymisiert)</li>
                  <li>Mausbewegungen, Klicks, Scroll-Verhalten</li>
                  <li>Besuchte Seiten und Seitenelemente</li>
                  <li>Browserinformationen und Bildschirmauflösung</li>
                  <li>Verweildauer auf Seiten</li>
                  <li>Session Recordings (Aufzeichnung der Sitzung)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">
                  Analyse des Nutzerverhaltens zur Verbesserung der Benutzerfreundlichkeit (UX), Identifikation von Problemen in der Nutzerführung, Optimierung der Website-Struktur
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. a DSGVO (Einwilligung über Cookie-Banner)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Projekt-ID</h4>
                <p className="text-gray-700 text-sm font-mono">ujffk11rf1</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">
                  Cookies: Bis zu 1 Jahr<br />
                  Session-Daten: Gemäß Microsoft Clarity Richtlinien (typischerweise 30-90 Tage)
                </p>
              </div>
              <div className="p-4 bg-orange-50 border border-orange-200 rounded">
                <p className="text-sm text-orange-800 mb-2">
                  <strong>⚠️ Datenübermittlung in die USA:</strong>
                </p>
                <p className="text-sm text-orange-800">
                  Microsoft verarbeitet Daten auch in den USA. Microsoft ist nach dem EU-US Data Privacy Framework zertifiziert, welches ein angemessenes Datenschutzniveau gewährleistet.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Widerruf</h4>
                <p className="text-gray-700 text-sm">
                  Du kannst deine Einwilligung jederzeit über die Cookie-Einstellungen (Footer-Link) widerrufen oder Cookies in deinen Browser-Einstellungen löschen.
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen:{' '}
                  <a href="https://privacy.microsoft.com/de-de/privacystatement" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Microsoft Datenschutzerklärung
                  </a>
                  {' | '}
                  <a href="https://learn.microsoft.com/en-us/clarity/setup-and-installation/privacy-disclosure" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Clarity Privacy Disclosure
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 8. Google Analytics 4 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-600" />
                8. Google Analytics 4 (Website-Analyse)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Beschreibung</h4>
                <p className="text-gray-700 text-sm">
                  Wir nutzen Google Analytics 4, um zu verstehen, wie Nutzer mit unserer Website interagieren, welche Inhalte am häufigsten aufgerufen werden und wie wir unsere Website verbessern können.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">
                  Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland<br />
                  Muttergesellschaft: Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>IP-Adresse (anonymisiert)</li>
                  <li>Browserinformationen (Typ, Version, Sprache)</li>
                  <li>Besuchte Seiten, Verweildauer und Seitenaufrufe</li>
                  <li>Referrer-URL (Herkunft des Besuchs)</li>
                  <li>Gerätetyp, Betriebssystem und Bildschirmauflösung</li>
                  <li>Nutzerinteraktionen (Klicks, Scrolling, Downloads)</li>
                  <li>Geografische Herkunft (Land, Region)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">
                  Analyse des Nutzerverhaltens, Messung der Website-Performance, Optimierung von Inhalten und Nutzererfahrung, Erstellung von Statistiken und Reports
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. a DSGVO (Einwilligung über Cookie-Banner)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Measurement-ID</h4>
                <p className="text-gray-700 text-sm font-mono">G-8Q9R1YT6JP</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">
                  Cookies: Bis zu 2 Jahre<br />
                  User-Daten bei Google: 2 Monate (automatische Löschung nach Ablauf)
                </p>
              </div>
              <div className="p-4 bg-orange-50 border border-orange-200 rounded">
                <p className="text-sm text-orange-800 mb-2">
                  <strong>⚠️ Datenübermittlung in die USA:</strong>
                </p>
                <p className="text-sm text-orange-800">
                  Google LLC verarbeitet Daten auch in den USA. Google ist nach dem EU-US Data Privacy Framework zertifiziert, welches ein angemessenes Datenschutzniveau gewährleistet.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">IP-Anonymisierung</h4>
                <p className="text-gray-700 text-sm">
                  Google Analytics 4 anonymisiert IP-Adressen standardmäßig. Die vollständige IP-Adresse wird nicht gespeichert.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Widerruf</h4>
                <p className="text-gray-700 text-sm">
                  Du kannst deine Einwilligung jederzeit über die Cookie-Einstellungen (Footer-Link) widerrufen. Alternativ kannst du das Browser-Add-on zur Deaktivierung von Google Analytics verwenden.
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen:{' '}
                  <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Google Datenschutzerklärung
                  </a>
                  {' | '}
                  <a href="https://support.google.com/analytics/answer/12017362" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    GA4 Datenerfassung
                  </a>
                  {' | '}
                  <a href="https://tools.google.com/dlpage/gaoptout" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Google Analytics Opt-out
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 9. Google Ads Tracking */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-orange-600" />
                9. Google Ads Conversion-Tracking
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Beschreibung</h4>
                <p className="text-gray-700 text-sm">
                  Wir nutzen Google Ads Conversion-Tracking, um die Wirksamkeit unserer Werbekampagnen zu messen. Dabei wird erfasst, ob du über eine Google-Anzeige auf unsere Website gelangt bist und anschließend eine bestimmte Aktion durchgeführt hast (z.B. Registrierung).
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">
                  Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland<br />
                  Muttergesellschaft: Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>IP-Adresse (anonymisiert)</li>
                  <li>Klick-Informationen (Anzeigen-ID, Keywords)</li>
                  <li>Besuchte Seiten</li>
                  <li>Conversion-Events (z.B. Registrierung)</li>
                  <li>Gerätetyp und Browserinformationen</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">
                  Messung der Wirksamkeit unserer Google Ads Kampagnen, Optimierung der Werbeausgaben, Erfolgskontrolle
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. a DSGVO (Einwilligung über Cookie-Banner)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">
                  Cookies: 90 Tage<br />
                  Conversion-Daten bei Google: Gemäß Google Ads Richtlinien
                </p>
              </div>
              <div className="p-4 bg-orange-50 border border-orange-200 rounded">
                <p className="text-sm text-orange-800 mb-2">
                  <strong>⚠️ Datenübermittlung in die USA:</strong>
                </p>
                <p className="text-sm text-orange-800">
                  Google LLC verarbeitet Daten auch in den USA. Google ist nach dem EU-US Data Privacy Framework zertifiziert, welches ein angemessenes Datenschutzniveau gewährleistet.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Widerruf</h4>
                <p className="text-gray-700 text-sm">
                  Du kannst deine Einwilligung jederzeit über die Cookie-Einstellungen (Footer-Link) widerrufen oder Cookies in deinen Browser-Einstellungen löschen.
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen:{' '}
                  <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Google Datenschutzerklärung
                  </a>
                  {' | '}
                  <a href="https://support.google.com/google-ads/answer/2407785" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Google Ads Tracking
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 10. Google OAuth */}
          <Card>
            <CardHeader>
              <CardTitle>10. Google OAuth (Login)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>Google-ID</li>
                  <li>E-Mail-Adresse</li>
                  <li>Name</li>
                  <li>Profilbild (optional)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Vereinfachte Registrierung und Anmeldung</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">Bis zur Kontolöschung oder Widerruf der OAuth-Berechtigung über dein Google-Konto</p>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen: <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Datenschutzerklärung</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 11. Stripe */}
          <Card>
            <CardHeader>
              <CardTitle>11. Zahlungsdienste (Stripe)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">Stripe Payments Europe Ltd., 1 Grand Canal Street Lower, Grand Canal Dock, Dublin, D02 H210, Irland</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>Name und E-Mail-Adresse</li>
                  <li>Rechnungsadresse</li>
                  <li>Zahlungsinformationen (Kreditkarte, IBAN)</li>
                  <li>Transaktionsdaten</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Abwicklung von Zahlungsvorgängen für kostenpflichtige Dienste</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">Gemäß gesetzlicher Aufbewahrungsfristen (7 Jahre für Buchhaltungsdaten)</p>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen: <a href="https://stripe.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 11.5 Öffentlicher Rechnungsgenerator */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-emerald-600" />
                11.5 Öffentlicher Rechnungsgenerator
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Beschreibung</h4>
                <p className="text-gray-700 text-sm">
                  ivoliq bietet einen kostenlosen, öffentlich zugänglichen Rechnungsgenerator an (erreichbar unter /rechnungsvorlage-freelancer-deutschland, -oesterreich, -schweiz). 
                  Dieser ermöglicht es Besuchern, ohne Registrierung professionelle Rechnungen als PDF zu erstellen.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">11.5.1 Rechnungserstellung (ohne E-Mail)</h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Verarbeitete Daten:</p>
                    <p className="text-gray-700 text-sm">
                      <strong>Keine.</strong> Die Rechnungserstellung erfolgt vollständig im Browser (Client-Side). 
                      Die eingegebenen Rechnungsdaten (Absender, Empfänger, Positionen) werden <strong>nicht</strong> an unsere Server übermittelt.
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 border border-green-200 rounded">
                    <p className="text-sm text-green-800">
                      <strong>✓ Datenschutzfreundlich:</strong> Die PDF-Generierung erfolgt lokal in deinem Browser mittels JavaScript. 
                      Deine Geschäftsdaten verlassen niemals deinen Computer.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">11.5.2 Optionale E-Mail-Bestätigung</h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Beschreibung:</p>
                    <p className="text-gray-700 text-sm">
                      Nach der Rechnungserstellung kannst du optional eine Bestätigungs-E-Mail anfordern. 
                      Diese Funktion erfordert deine ausdrückliche Einwilligung.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Verarbeitete Daten:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>E-Mail-Adresse</li>
                      <li>Region (DE/AT/CH)</li>
                      <li>Einwilligungsstatus für Marketing-E-Mails</li>
                      <li>Gehashte IP-Adresse (zum Schutz vor Missbrauch, nicht zurückverfolgbar)</li>
                      <li>Zeitstempel der Anfrage</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Zweck:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Versand einer Bestätigungs-E-Mail mit Informationen zum Tool</li>
                      <li>Bei Einwilligung: Versand von Produkt-Updates und Tipps (max. 1x monatlich)</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Rechtsgrundlage:</p>
                    <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. a DSGVO (ausdrückliche Einwilligung durch Checkbox)</p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Speicherdauer:</p>
                    <p className="text-gray-700 text-sm">
                      Deine E-Mail-Adresse wird gespeichert, bis du dich vom Newsletter abmeldest oder die Löschung beantragst.
                      Die gehashte IP-Adresse wird nach 30 Tagen automatisch gelöscht.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Widerruf:</p>
                    <p className="text-gray-700 text-sm">
                      Du kannst deine Einwilligung jederzeit widerrufen, indem du auf „Abmelden" in jeder E-Mail klickst 
                      oder uns unter datenschutz@ivoliq.io kontaktierst.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">E-Mail-Versand:</p>
                    <p className="text-gray-700 text-sm">
                      Die E-Mails werden über Brevo (siehe Abschnitt 12) von der Adresse billing@ivoliq.io versendet.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Hinweis:</strong> Die eingegebenen Rechnungsdaten (Firmenname, Adresse, Rechnungspositionen etc.) 
                  werden <strong>nicht</strong> gespeichert, auch nicht bei Nutzung der E-Mail-Funktion.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 11.6 Cal.com Terminbuchung */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-blue-600" />
                11.6 Terminbuchung (Cal.com)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Beschreibung</h4>
                <p className="text-gray-700 text-sm">
                  Für die Buchung kostenloser Produktdemos verwenden wir den Dienst Cal.com. 
                  Wenn du über unsere Website einen Demo-Termin buchst, werden deine Daten an Cal.com übermittelt.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Erhobene Daten</h4>
                <ul className="text-sm text-gray-700 space-y-1 list-disc pl-5">
                  <li>Name</li>
                  <li>E-Mail-Adresse</li>
                  <li>Gewählter Termin (Datum und Uhrzeit)</li>
                  <li>Ggf. weitere Angaben, die du freiwillig im Buchungsformular machst</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck der Verarbeitung</h4>
                <ul className="text-sm text-gray-700 space-y-1 list-disc pl-5">
                  <li>Koordination und Durchführung der Produktdemo</li>
                  <li>Versand von Terminbestätigungen und -erinnerungen</li>
                  <li>Kontaktaufnahme im Zusammenhang mit der gebuchten Demo (Follow-up)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">
                  Art. 6 Abs. 1 lit. b DSGVO (Durchführung vorvertraglicher Maßnahmen auf Anfrage der betroffenen Person) 
                  sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Nachverfolgung von Demo-Anfragen).
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Auftragsverarbeiter</h4>
                <p className="text-gray-700 text-sm">
                  Cal.com, Inc.<br />
                  Sitz: USA
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  Die bei der Terminbuchung angegebenen Kontaktdaten werden zur weiteren Kommunikation 
                  in unserem E-Mail-System (Brevo) als Lead gespeichert. Es erfolgt <strong>kein Versand von 
                  Marketing-E-Mails</strong> ohne gesonderte Einwilligung.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">
                  Die Daten werden für die Dauer der Geschäftsbeziehung gespeichert und anschließend 
                  gemäß gesetzlichen Aufbewahrungsfristen gelöscht oder anonymisiert. Du kannst jederzeit 
                  die Löschung deiner Daten verlangen (siehe Betroffenenrechte).
                </p>
              </div>
              <p className="text-sm text-gray-700">
                Weitere Informationen:{' '}
                <a href="https://cal.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                  Cal.com Datenschutzerklärung
                </a>
              </p>
            </CardContent>
          </Card>

          {/* 12. E-Mail-Versand */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                12. E-Mail-Versand (Rechnungen)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Beschreibung</h4>
                <p className="text-gray-700 text-sm">
                  ivoliq bietet dir die Möglichkeit, Rechnungen direkt per E-Mail an deine Kunden zu versenden. Hierfür stehen zwei Versandmethoden zur Verfügung:
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">12.1 Standard-Versand über Brevo (alle Pläne)</h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Anbieter:</p>
                    <p className="text-gray-700 text-sm">
                      Brevo (ehemals Sendinblue)<br />
                      Brevo SAS, 7 rue de Madrid, 75008 Paris, Frankreich<br />
                      EU-Repräsentant: Brevo GmbH, Köpenicker Str. 126, 10179 Berlin, Deutschland
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Verarbeitete Daten:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>E-Mail-Adresse des Empfängers (dein Kunde)</li>
                      <li>Name des Empfängers</li>
                      <li>Absendername (dein Firmenname)</li>
                      <li>E-Mail-Inhalt (Rechnungstext)</li>
                      <li>PDF-Anhang (Rechnung)</li>
                      <li>Versandzeitpunkt und -status</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Zweck:</p>
                    <p className="text-gray-700 text-sm">Versand von Rechnungen per E-Mail in deinem Auftrag</p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Rechtsgrundlage:</p>
                    <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Speicherdauer:</p>
                    <p className="text-gray-700 text-sm">
                      Bei Brevo: Gemäß Brevo-Richtlinien (typischerweise 30 Tage für Versandlogs)<br />
                      Bei ivoliq: Versandstatus wird in der Rechnung gespeichert (bis zur Kontolöschung)
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 border border-green-200 rounded">
                    <p className="text-sm text-green-800">
                      <strong>Serverstandort:</strong> EU (Frankreich/Deutschland)<br />
                      <strong>Auftragsverarbeitung:</strong> Mit Brevo wurde ein Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO geschlossen.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">12.2 Pro-Feature: Beleg-Eingang per E-Mail (Brevo Inbound)</h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Beschreibung:</p>
                    <p className="text-gray-700 text-sm">
                      Pro-Plan-Nutzer erhalten optional eine persönliche E-Mail-Adresse (z.B. name-xxxxxx@belege.ivoliq.io),
                      an die Belege gesendet oder weitergeleitet werden können. Eingehende E-Mails werden durch Brevo
                      (siehe 12.1) entgegengenommen, geparst und an unsere Server übermittelt. Anhänge (bzw. bei Mails
                      ohne Anhang der E-Mail-Text) werden als Beleg in deinem Konto gespeichert. Die KI-gestützte
                      Datenextraktion erfolgt ausschließlich mit deiner separaten Einwilligung (KI-Belegerkennung,
                      Abschnitt 6). E-Mail-Metadaten (Absender, Betreff, Eingangszeitpunkt) werden zum Herkunftsnachweis
                      am Beleg gespeichert. Anhänge werden bei Brevo nur vorübergehend vorgehalten und unmittelbar nach
                      Eingang auf unsere Server übertragen.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Rechtsgrundlage:</p>
                    <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung — Funktion wird aktiv vom Nutzer genutzt); Auftragsverarbeitung durch Brevo gemäß Art. 28 DSGVO (AVV, siehe 12.1)</p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Speicherdauer:</p>
                    <p className="text-gray-700 text-sm">Belege und Metadaten folgen der regulären Beleg-Aufbewahrung (bis zur Kontolöschung bzw. GoBD-Fristen); Original-E-Mails werden nicht dauerhaft gespeichert.</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">12.3 Pro-Feature: Versand über eigenes Gmail (nur Pro-Plan)</h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Beschreibung:</p>
                    <p className="text-gray-700 text-sm">
                      Pro-Plan Nutzer können optional ihr eigenes Gmail-Konto für den E-Mail-Versand verwenden. Dies erfolgt über Google OAuth 2.0.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Anbieter:</p>
                    <p className="text-gray-700 text-sm">
                      Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland<br />
                      Muttergesellschaft: Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Verarbeitete Daten:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Gmail OAuth-Token (verschlüsselt gespeichert)</li>
                      <li>E-Mail-Adresse des Gmail-Kontos</li>
                      <li>Versendete E-Mails (über Gmail API)</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Zweck:</p>
                    <p className="text-gray-700 text-sm">Versand von Rechnungen über dein eigenes Gmail-Konto</p>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Rechtsgrundlage:</p>
                    <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. a DSGVO (Einwilligung durch OAuth-Autorisierung)</p>
                  </div>
                  
                  {/* Google Limited Use Disclosure - Required for Google App Verification */}
                  {/* English Version - Required by Google API Services User Data Policy */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded mb-4">
                    <p className="text-sm text-blue-900 font-bold mb-2">
                      🔐 Google API Services User Data Policy Compliance
                    </p>
                    <p className="text-sm text-blue-800 mb-3">
                      <strong>ivoliq's use and transfer to any other app of information received from Google APIs will adhere to the{' '}
                      <a href="https://developers.google.com/terms/api-services-user-data-policy" className="text-blue-600 hover:underline font-semibold" target="_blank" rel="noopener noreferrer">
                        Google API Services User Data Policy
                      </a>, including the Limited Use requirements.</strong>
                    </p>
                    <div className="text-sm text-blue-800 space-y-2">
                      <p><strong>Requested OAuth Scopes:</strong></p>
                      <ul className="list-disc ml-5 space-y-1">
                        <li><code className="bg-blue-100 px-1 rounded">openid</code> - Basic authentication</li>
                        <li><code className="bg-blue-100 px-1 rounded">email</code> - View your email address</li>
                        <li><code className="bg-blue-100 px-1 rounded">profile</code> - View your basic profile info</li>
                        <li><code className="bg-blue-100 px-1 rounded">https://www.googleapis.com/auth/gmail.send</code> - Send email on your behalf (Pro users only, optional)</li>
                      </ul>
                      <p className="mt-2"><strong>Limited Use Disclosure:</strong></p>
                      <ul className="list-disc ml-5 space-y-1">
                        <li>ivoliq <strong>only uses</strong> Google user data for the specific features you authorize (login authentication and optional email sending)</li>
                        <li>ivoliq <strong>does not transfer</strong> Google user data to third parties, except as necessary to provide our services or as required by law</li>
                        <li>ivoliq <strong>does not use</strong> Google user data for advertising purposes</li>
                        <li>ivoliq <strong>does not sell</strong> Google user data</li>
                        <li>Human access to Google user data is limited to system administrators for technical support only</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* German Version */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded">
                    <p className="text-sm text-green-800 font-semibold mb-2">
                      🔒 Weitergabe & Nutzung von Google-Nutzerdaten (Limited Use Disclosure)
                    </p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-green-800">
                      <li><strong>Keine Weitergabe an Dritte:</strong> ivoliq gibt von Google erhaltene Nutzerdaten (OAuth-Tokens, E-Mail-Adresse) <strong>nicht</strong> an Dritte weiter, verkauft diese nicht und verwendet sie nicht für Werbezwecke.</li>
                      <li><strong>Beschränkte Nutzung:</strong> Die Google-Nutzerdaten werden ausschließlich für den von dir autorisierten Zweck verwendet – den Versand von Rechnungs-E-Mails über dein Gmail-Konto.</li>
                      <li><strong>Sichere Speicherung:</strong> OAuth-Tokens werden AES-256 verschlüsselt in unserer EU-Datenbank (MongoDB Atlas, Frankfurt) gespeichert.</li>
                      <li><strong>Zugriffskontrolle:</strong> Nur autorisierte Systemadministratoren haben bei technischen Problemen Zugriff auf verschlüsselte Token-Daten. Entschlüsselung erfolgt nur automatisiert durch das System beim E-Mail-Versand.</li>
                      <li><strong>Keine Übertragung:</strong> Google-Nutzerdaten werden nicht an andere Dienste, Anwendungen oder Unterauftragnehmer übertragen – mit Ausnahme der Google Gmail API selbst für den E-Mail-Versand.</li>
                      <li><strong>Löschung:</strong> Bei Widerruf der Gmail-Verknüpfung oder Kontolöschung werden alle Google-bezogenen Daten unwiderruflich gelöscht.</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded">
                    <p className="text-sm text-orange-800 mb-2">
                      <strong>⚠️ Datenübermittlung in die USA:</strong>
                    </p>
                    <p className="text-sm text-orange-800">
                      Google LLC verarbeitet Daten auch in den USA. Google ist nach dem EU-US Data Privacy Framework zertifiziert.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">12.4 Wichtige Hinweise</h4>
                <div className="space-y-2">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-800">
                      <strong>Kein Tracking:</strong> ivoliq verwendet KEINE E-Mail-Tracking-Technologien (keine Öffnungs- oder Klick-Verfolgung).
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-800">
                      <strong>Auftragsverarbeitung:</strong> Du bist der Verantwortliche für die Kundendaten, ivoliq tritt als Auftragsverarbeiter auf.
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-800">
                      <strong>Versandlimits:</strong> Je nach Plan gelten monatliche E-Mail-Limits (siehe AGB).
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen:{' '}
                  <a href="https://www.brevo.com/de/legal/privacypolicy/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Brevo Datenschutzerklärung
                  </a>
                  {' | '}
                  <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Google Datenschutzerklärung
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 13. App-Nutzungsdaten */}
          <Card>
            <CardHeader>
              <CardTitle>13. App-Nutzungsdaten (deine Geschäftsdaten)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Kundendaten:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Name, Adresse, E-Mail, Telefonnummer deiner Kunden</li>
                      <li>UID-Nummer / Umsatzsteuer-Identifikationsnummer</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Projektdaten:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Projektname, Beschreibung</li>
                      <li>Stundensätze, Budget</li>
                      <li>Projektstatus</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Zeiterfassungen:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Datum, Uhrzeit (Start/Ende), Dauer</li>
                      <li>Tätigkeitsbeschreibung</li>
                      <li>Projektzuordnung</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Rechnungsdaten:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Rechnungsnummer, Beträge (Netto/Brutto)</li>
                      <li>Rechnungspositionen, Steuersätze</li>
                      <li>Zahlungsstatus, Fälligkeitsdatum</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Finanzdaten:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Einnahmen und Ausgaben</li>
                      <li>Kategorien, Belege</li>
                      <li>Steuerberechnungen</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Bereitstellung der SaaS-Funktionalität zur Verwaltung deiner Geschäftsdaten (Zeiterfassung, Kundenverwaltung, Rechnungserstellung, Steuerkalkulation)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">Bis zur Kontolöschung. <strong>Ausnahme:</strong> Rechnungsdaten werden gemäß § 147 AO (DE) bzw. § 132 BAO (AT) für 7 Jahre aufbewahrt (gesetzliche Aufbewahrungspflicht).</p>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Wichtig:</strong> Du bist für die von dir eingegebenen Kundendaten selbst verantwortlich (du bist der Verantwortliche i.S.d. DSGVO). ivoliq tritt als Auftragsverarbeiter auf. Bitte beachte unseren Auftragsverarbeitungsvertrag (AVV).
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 14. IP-Geolocation */}
          <Card>
            <CardHeader>
              <CardTitle>14. IP-Geolocation (Standorterkennung)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Beschreibung</h4>
                <p className="text-gray-700 text-sm">Wir verwenden IP-Geolocation, um dein Land automatisch zu erkennen und die korrekte steuerliche Behandlung sicherzustellen. Hierfür setzen wir primär <strong>ipapi.co</strong> und als technischen Fallback <strong>ip-api.com</strong> ein.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>IP-Adresse (nicht gespeichert)</li>
                  <li>Erkanntes Land (gespeichert)</li>
                  <li>Technischer Request an ipapi.co bzw. ip-api.com zur Länderauflösung</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Automatische Erkennung der Steueransässigkeit für korrekte Steuerberechnungen und passende DACH-Konfiguration beim Onboarding</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an korrekter regionaler Steuer- und Produktkonfiguration)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">Die IP-Adresse wird nicht gespeichert. Der ermittelte Ländercode wird in deinem Benutzerkonto gespeichert, bis du dein Konto löschst.</p>
              </div>
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-800">
                  <strong>Hinweis:</strong> Deine IP-Adresse wird nicht gespeichert, nur der erkannte Land-Code wird in deinem Profil hinterlegt.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">ipapi.co (Primary) sowie ip-api.com (Fallback), jeweils nur zur Ländererkennung auf Basis der IP-Adresse.</p>
              </div>
            </CardContent>
          </Card>

          {/* 14.5 Steuerberater-Zusammenarbeit */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                14.5 Steuerberater-Zusammenarbeit
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Beschreibung</h4>
                <p className="text-gray-700 text-sm">
                  ivoliq bietet eine optionale Zusammenarbeitsfunktion zwischen Freelancern und Steuerberatungskanzleien.
                  Die Verbindung kommt auf drei Wegen zustande: (a) der Freelancer l&auml;dt seine Kanzlei ein,
                  (b) die Kanzlei l&auml;dt den Freelancer ein (Annahme durch den Freelancer erforderlich), oder
                  (c) die Kanzlei legt den Account f&uuml;r den Freelancer an — der Vertrag kommt dann erst mit der
                  Aktivierung durch den Freelancer (Passwort + Zustimmung zu AGB und Datenschutzerkl&auml;rung) zustande.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Berechtigungen (Vollmachten)</h4>
                <p className="text-gray-700 text-sm">
                  Die Kanzlei erh&auml;lt nur die ihr erteilten Berechtigungen (z.B. Einsicht in &Uuml;bersicht, Rechnungen,
                  Ausgaben, Belege, Steuerdaten oder eine Bearbeiten-Vollmacht f&uuml;r Kategorien und Korrekturen).
                  Bei Account-Anlage durch die Kanzlei werden die erteilten Vollmachten in der Aktivierungs-E-Mail
                  ausdr&uuml;cklich aufgelistet. Der Freelancer kann alle Berechtigungen jederzeit in den Einstellungen
                  unter „Steuerberater-Zugriff" einschr&auml;nken oder vollst&auml;ndig widerrufen.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Geteilte Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>Einnahmen und Ausgaben (inkl. Belege)</li>
                  <li>Rechnungsdaten und -status</li>
                  <li>Steuerberechnungen und -einstellungen</li>
                  <li>Aufgaben und Kommentare innerhalb der Zusammenarbeit</li>
                  <li>Dokumentenanfragen und hochgeladene Dateien</li>
                  <li>Periodenpr&uuml;fungen und deren Status</li>
                  <li>Lizenzstatus (bei Kanzlei-Lizenz: Information, dass die Kanzlei die Lizenzkosten tr&auml;gt)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">
                  Erm&ouml;glichung einer effizienten Zusammenarbeit zwischen Freelancer und Steuerberater 
                  f&uuml;r die steuerliche Betreuung, Belegpr&uuml;fung und Datenexporte (CSV, DATEV, BMD, ZIP).
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. b DSGVO (Vertragserf&uuml;llung) sowie Art. 6 Abs. 1 lit. a DSGVO (Einwilligung durch aktive Einladung bzw. Annahme/Aktivierung)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Kontrolle</h4>
                <p className="text-gray-700 text-sm">
                  Der Freelancer kann die Zusammenarbeit jederzeit beenden. Nach Beendigung hat der Steuerberater 
                  keinen Zugriff mehr auf die Daten. Bereits exportierte Daten verbleiben beim Steuerberater 
                  und unterliegen dessen eigener Datenschutzverantwortung.
                </p>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Wichtig:</strong> Es findet keine automatische Datenweitergabe statt — jede Verbindung
                  erfordert eine aktive Handlung des Freelancers (Einladung, Annahme oder Account-Aktivierung).
                  F&uuml;r die Verarbeitung von Mandantendaten durch die Kanzlei &uuml;ber das SB-Portal gilt der{' '}
                  <a href="/steuerberater-avv" className="underline hover:text-blue-900">SB-Auftragsverarbeitungsvertrag</a>;
                  die Kanzlei ist insoweit eigenst&auml;ndige datenschutzrechtliche Verantwortliche.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 15. Hosting */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-600" />
                15. Hosting
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, Deutschland</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <p className="text-gray-700 text-sm">Alle Daten, die auf unserer Website verarbeitet werden</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Sicheres und zuverlässiges Hosting unserer Website</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. f DSGVO (Berechtigtes Interesse)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Auftragsverarbeitung</h4>
                <p className="text-gray-700 text-sm">Mit Hetzner wurde ein Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO geschlossen.</p>
              </div>
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-800">
                  <strong>Serverstandort:</strong> Deutschland / EU
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen: <a href="https://www.hetzner.com/rechtliches/datenschutz" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Hetzner Datenschutzerklärung</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 16. Auftragsverarbeitungsvertrag (AVV) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                16. Auftragsverarbeitungsvertrag (AVV) / Data Processing Agreement (DPA)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Brevo AVV/DPA</h4>
                <p className="text-gray-700 text-sm mb-3">
                  Für den E-Mail-Versand über Brevo wurde ein Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO geschlossen.
                </p>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>Wichtiger Hinweis für Nutzer:</strong>
                  </p>
                  <p className="text-sm text-blue-800">
                    Als Nutzer von ivoliq solltest du prüfen, ob du zusätzlich einen direkten AVV/DPA mit Brevo abschließen möchtest, 
                    insbesondere wenn du sensible Kundendaten per E-Mail versendest. Dies ist abhängig von deiner individuellen 
                    Risikoeinschätzung und den Anforderungen deiner Kunden.
                  </p>
                </div>
                <p className="text-sm text-gray-700 mt-3">
                  Brevo DPA-Informationen:{' '}
                  <a href="https://www.brevo.com/de/legal/data-processing-agreement/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    https://www.brevo.com/de/legal/data-processing-agreement/
                  </a>
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Hetzner AVV</h4>
                <p className="text-gray-700 text-sm">
                  Für das Hosting wurde ein Auftragsverarbeitungsvertrag mit Hetzner Online GmbH gemäß Art. 28 DSGVO geschlossen.
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  Weitere Informationen:{' '}
                  <a href="https://www.hetzner.com/rechtliches/datenschutz" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Hetzner Datenschutzerklärung
                  </a>
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Cal.com DPA</h4>
                <p className="text-gray-700 text-sm">
                  Für die Terminbuchung über Cal.com werden personenbezogene Daten (Name, E-Mail, Termin) 
                  an Cal.com, Inc. übermittelt. Cal.com verarbeitet diese Daten als Auftragsverarbeiter.
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  Cal.com DPA-Informationen:{' '}
                  <a href="https://cal.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    https://cal.com/privacy
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 17. Betroffenenrechte */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-blue-600" />
                17. Deine Rechte (Betroffenenrechte)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Du hast folgende Rechte:</h4>
                  <ul className="space-y-3 text-sm text-gray-700">
                    <li>
                      <strong>Recht auf Auskunft (Art. 15 DSGVO):</strong><br />
                      Du hast das Recht, Auskunft über deine gespeicherten personenbezogenen Daten zu erhalten.
                    </li>
                    <li>
                      <strong>Recht auf Berichtigung (Art. 16 DSGVO):</strong><br />
                      Du hast das Recht, unrichtige Daten berichtigen zu lassen.
                    </li>
                    <li>
                      <strong>Recht auf Löschung (Art. 17 DSGVO):</strong><br />
                      Du hast das Recht, deine Daten löschen zu lassen ("Recht auf Vergessenwerden").
                      <div className="mt-2 ml-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                        <p className="font-semibold mb-2">Löschprozess:</p>
                        <ol className="list-decimal list-inside space-y-1">
                          <li>Du kannst dein Konto jederzeit über die Einstellungen löschen</li>
                          <li>Nach Löschung: 30 Tage Wiederherstellungsfrist (Soft-Delete)</li>
                          <li>Nach Ablauf der Frist: Endgültige Löschung aller Daten binnen 7 Tagen</li>
                          <li><strong>Ausnahme:</strong> Rechnungsdaten werden gemäß § 147 AO (DE) bzw. § 132 BAO (AT) für 7 Jahre aufbewahrt (gesetzliche Aufbewahrungspflicht)</li>
                          <li>Logfiles werden nach 7 Tagen automatisch gelöscht</li>
                        </ol>
                      </div>
                    </li>
                    <li>
                      <strong>Recht auf Einschränkung (Art. 18 DSGVO):</strong><br />
                      Du hast das Recht, die Verarbeitung einschränken zu lassen.
                    </li>
                    <li>
                      <strong>Recht auf Datenübertragbarkeit (Art. 20 DSGVO):</strong><br />
                      Du hast das Recht, deine Daten in einem strukturierten Format zu erhalten.
                    </li>
                    <li>
                      <strong>Widerspruchsrecht (Art. 21 DSGVO):</strong><br />
                      Du hast das Recht, der Datenverarbeitung zu widersprechen.
                    </li>
                    <li>
                      <strong>Recht auf Widerruf (Art. 7 DSGVO):</strong><br />
                      Du kannst erteilte Einwilligungen jederzeit widerrufen.
                    </li>
                    <li>
                      <strong>Beschwerderecht (Art. 77 DSGVO):</strong><br />
                      Du hast das Recht, dich bei einer Aufsichtsbehörde zu beschweren.
                    </li>
                  </ul>
                </div>
                <div className="pt-4 border-t">
                  <h4 className="font-semibold text-gray-900 mb-2">Kontakt zur Ausübung deiner Rechte:</h4>
                  <p className="text-sm text-gray-700 mb-3">
                    Sende uns eine E-Mail an:{' '}
                    <a href="mailto:datenschutz@ivoliq.io" className="text-blue-600 hover:underline font-medium" data-testid="privacy-rights-contact-email">
                      datenschutz@ivoliq.io
                    </a>
                  </p>
                  <p className="text-sm text-gray-700">
                    Du kannst dein Konto auch direkt in den Einstellungen löschen.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 18. Aufsichtsbehörde */}
          <Card>
            <CardHeader>
              <CardTitle>18. Beschwerderecht bei der Aufsichtsbehörde</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-gray-700 text-sm">
                Du hast das Recht, dich bei der zuständigen Datenschutz-Aufsichtsbehörde zu beschweren:
              </p>
              <div className="p-4 bg-gray-50 border border-gray-200 rounded">
                <p className="font-semibold text-gray-900">Österreichische Datenschutzbehörde</p>
                <p className="text-sm text-gray-700">Barichgasse 40-42</p>
                <p className="text-sm text-gray-700">1030 Wien, Österreich</p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>Telefon:</strong> +43 1 52 152-0
                </p>
                <p className="text-sm text-gray-700">
                  <strong>E-Mail:</strong>{' '}
                  <a href="mailto:dsb@dsb.gv.at" className="text-blue-600 hover:underline">
                    dsb@dsb.gv.at
                  </a>
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Website:</strong>{' '}
                  <a href="https://www.dsb.gv.at/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    https://www.dsb.gv.at/
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 19. Datensicherheit */}
          <Card>
            <CardHeader>
              <CardTitle>19. Datensicherheit</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-gray-700 text-sm">
                Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, um deine Daten gegen zufällige oder vorsätzliche Manipulationen, Verlust, Zerstörung oder den Zugriff unberechtigter Personen zu schützen.
              </p>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Unsere Maßnahmen:</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>SSL/TLS-Verschlüsselung (HTTPS)</li>
                  <li>Verschlüsselte Passwort-Speicherung (Hashing)</li>
                  <li>Zugriffskontrolle und Berechtigungskonzept</li>
                  <li>Regelmäßige Sicherheits-Updates</li>
                  <li>Firewall und Intrusion Detection</li>
                  <li>Regelmäßige Backups</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* 20. KI-gestützte Belegerkennung */}
          <Card>
            <CardHeader>
              <CardTitle>20. KI-gestützte Belegerkennung und Belegspeicherung</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <h4 className="font-semibold text-gray-800 text-sm mb-1">20.1 Lokale Belegerkennung (OCR)</h4>
                <p className="text-gray-700 text-sm">
                  Im Standard-Modus werden hochgeladene Belege (PDF, Bilder) ausschließlich lokal auf unseren Servern in Deutschland (Hetzner Online GmbH, Nürnberg) verarbeitet. Die Texterkennung erfolgt mittels Open-Source-Software (Tesseract OCR). Es findet keine Datenübermittlung an Dritte statt.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 text-sm mb-1">20.2 Erweiterte KI-Erkennung (optional, nur mit Einwilligung)</h4>
                <p className="text-gray-700 text-sm">
                  Nutzer des Pro-Plans können optional eine KI-gestützte Belegerkennung aktivieren. Dabei werden Belegdaten (Bilddaten, keine personenbezogenen Nutzerdaten) zur Verarbeitung an folgende Drittanbieter übermittelt:
                </p>
                <ul className="list-disc pl-5 text-gray-700 text-sm mt-2 space-y-1">
                  <li><strong>OpenAI, Inc.</strong> (San Francisco, USA) – GPT-4o Vision API</li>
                  <li><strong>Anthropic, PBC</strong> (San Francisco, USA) – Claude Vision API</li>
                </ul>
                <p className="text-gray-700 text-sm mt-2">
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (ausdrückliche Einwilligung). Die Einwilligung wird vor der ersten Nutzung eingeholt und kann jederzeit in den Einstellungen widerrufen werden. Bei Widerruf wird automatisch die lokale OCR-Verarbeitung verwendet.
                </p>
                <p className="text-gray-700 text-sm mt-2">
                  <strong>Datenschutzgarantien:</strong> Mit beiden Anbietern bestehen Auftragsverarbeitungsverträge (Data Processing Addendums) inkl. EU-Standardvertragsklauseln (SCCs) gemäß Art. 46 Abs. 2 lit. c DSGVO.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 text-sm mb-1">20.3 Belegspeicherung</h4>
                <p className="text-gray-700 text-sm">
                  Hochgeladene Belege werden verschlüsselt auf Hetzner Object Storage (S3-kompatibel) in Deutschland gespeichert. Die Speicherung erfolgt ausschließlich in EU-Rechenzentren. Belege können jederzeit vom Nutzer gelöscht werden. Eine automatische Löschung erfolgt nach Kontolöschung oder nach Ablauf der konfigurierbaren Aufbewahrungsfrist.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 21. lex.ivoliq Warteliste (Juli 2026 — Lead-Start) */}
          <Card>
            <CardHeader>
              <CardTitle>21. lex.ivoliq Warteliste (Early Access)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="privacy-lex-waitlist-section">
              <p className="text-gray-700 text-sm">
                Auf der Website unseres Produkts <strong>lex.ivoliq</strong> (lex.ivoliq.io) kannst du dich
                unverbindlich in eine Warteliste für den frühen Zugang eintragen. Dabei verarbeiten wir die
                von dir angegebenen Daten: <strong>Name</strong> und <strong>E-Mail-Adresse</strong>.
              </p>
              <p className="text-gray-700 text-sm">
                <strong>Zweck:</strong> Bestätigung deines Eintrags per E-Mail sowie persönliche Kontaktaufnahme
                zum Produktstart von lex.ivoliq (Information über Verfügbarkeit und bevorzugten Zugang).
                Eine darüber hinausgehende werbliche Nutzung oder Weitergabe an Dritte findet nicht statt.
              </p>
              <p className="text-gray-700 text-sm">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung durch aktives Absenden
                des Formulars) sowie Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen auf deine Anfrage).
              </p>
              <p className="text-gray-700 text-sm">
                <strong>Versand &amp; Speicherung:</strong> Die Bestätigungs-E-Mail wird über Brevo versendet
                (siehe Abschnitt 12). Die Speicherung erfolgt auf unseren Servern in der EU.
              </p>
              <p className="text-gray-700 text-sm">
                <strong>Speicherdauer &amp; Widerruf:</strong> Deine Daten werden bis zum Produktstart bzw. bis zu
                deinem Widerruf gespeichert und spätestens 12 Monate nach dem Start von lex.ivoliq gelöscht, sofern
                kein Nutzungsverhältnis zustande kommt. Du kannst deinen Eintrag jederzeit formlos per E-Mail an{" "}
                <a href="mailto:datenschutz@ivoliq.io" className="text-blue-600 hover:underline">datenschutz@ivoliq.io</a>{" "}
                widerrufen — wir löschen deine Daten dann unverzüglich.
              </p>
            </CardContent>
          </Card>

          {/* 22. Änderungen */}
          <Card>
            <CardHeader>
              <CardTitle>22. Änderungen dieser Datenschutzerklärung</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 text-sm">
                Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen. Für deinen erneuten Besuch gilt dann die neue Datenschutzerklärung.
              </p>
            </CardContent>
          </Card>

          {/* Footer Info */}
          <div className="text-center py-6">
            <p className="text-sm text-gray-500">
              Bei Fragen zur Verarbeitung deiner Daten kontaktiere uns unter:{' '}
              <a href="mailto:datenschutz@ivoliq.io" className="text-blue-600 hover:underline font-medium" data-testid="privacy-footer-contact-email">
                datenschutz@ivoliq.io
              </a>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}